import React, { useState } from 'react';
import { CATEGORIES } from '../../utils/themes';
import { getDatabase, ref, update } from 'firebase/database';
import { useAuth } from '../../contexts/AuthContext';
import { Check, ArrowRight } from 'lucide-react';

interface ThemeOnboardingProps {
  onComplete: () => void;
}

export default function ThemeOnboarding({ onComplete }: ThemeOnboardingProps) {
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);

  const toggleCategory = (categoryId: string) => {
    setSelectedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleContinue = async () => {
    if (selectedCategories.length === 0) return;

    setLoading(true);
    try {
      if (currentUser) {
        const db = getDatabase();
        const userRef = ref(db, `users/${currentUser.uid}/preferences`);
        await update(userRef, {
          interests: selectedCategories,
          onboardingCompleted: true,
          updatedAt: Date.now()
        });
      } else {
        localStorage.setItem('guest_preferences', JSON.stringify({
          interests: selectedCategories,
          onboardingCompleted: true,
          updatedAt: Date.now()
        }));
      }
      onComplete();
    } catch (error) {
      console.error('Error saving preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            What do you want to learn?
          </h1>
          <p className="text-gray-600 text-lg">
            Select the topics that interest you the most. We'll personalize your learning path.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          {CATEGORIES.map(category => {
            const isSelected = selectedCategories.includes(category.id);
            return (
              <button
                key={category.id}
                onClick={() => toggleCategory(category.id)}
                className={`flex flex-col items-center justify-center p-6 rounded-xl border-2 transition-all duration-200 ${
                  isSelected
                    ? 'border-purple-600 bg-purple-50 text-purple-700'
                    : 'border-gray-200 hover:border-purple-200 text-gray-600'
                }`}
              >
                <span className="text-4xl mb-3">{category.icon}</span>
                <span className="font-medium text-center">{category.label}</span>
                {isSelected && (
                  <div className="absolute top-2 right-2">
                    <Check className="w-5 h-5 text-purple-600" />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex justify-center">
          <button
            onClick={handleContinue}
            disabled={selectedCategories.length === 0 || loading}
            className={`flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-lg transition-all ${
              selectedCategories.length > 0 && !loading
                ? 'bg-purple-600 text-white hover:bg-purple-700 shadow-lg hover:shadow-xl transform hover:-translate-y-1'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {loading ? 'Saving...' : 'Start Learning'}
            {!loading && <ArrowRight className="w-6 h-6" />}
          </button>
        </div>
      </div>
    </div>
  );
}
